// client/src/store/thunks/directServicesThunks.js
import {
  setHealthCheckLoading,
  setRestLoading,
  addLog,
  updateNetworkDiagnostics,
  setRabbitMQStatus,
  setTarantoolStatus,
  setMongoDBStatus,
  setClickHouseStatus,
  setFeatureTreeStatus,
  setKratosPublicStatus,
  setKratosAdminStatus,
  setKratosUsersStatus,
} from '../slices/servicesTesterSlice';

import {
  SERVICE_URLS,
  API_ENDPOINTS,
  DIRECT_SERVICES,
  HTTP_METHODS,
  STATUS_TYPES,
  TIMEOUTS,
} from '../constants/servicesTesterConstants';

// ============================================================================
// Вспомогательная функция для безопасных fetch запросов
// ============================================================================

const safeFetch = async (url, options = {}) => {
  const controller = new AbortController();
  const timeout = options.timeout || TIMEOUTS.QUICK_CHECK;
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if ([401, 403, 404].includes(response.status)) {
        return {
          _error: true,
          status: response.status,
          statusText: response.statusText,
          url,
        };
      }
      throw new Error(`HTTP ${response.status} - ${response.statusText}`);
    }

    try {
      const data = await response.json();
      return data;
    } catch (parseError) {
      const text = await response.text();
      return {
        _type: 'text',
        content: text,
        status: response.status,
      };
    }
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error(`Таймаут запроса (${timeout}ms): ${url}`);
    }
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error(`Сервис недоступен: ${url}`);
    }
    if (error.name === 'TypeError' && error.message.includes('CORS')) {
      throw new Error(`CORS ошибка: ${url}`);
    }
    throw error;
  }
};

// ==================== ОСНОВНЫЕ THUNKS ====================

export const performQuickHealthCheck = () => async (dispatch, getState) => {
  dispatch(setHealthCheckLoading(true));

  dispatch(
    addLog({
      msg: '🏥 Запуск быстрой проверки сервисов...',
      type: 'info',
    })
  );

  try {
    const healthResults = {};

    const healthPromises = DIRECT_SERVICES.map(async (service) => {
      try {
        const healthUrl = `${service.url}${service.healthPath}`;
        const startTime = Date.now();

        const response = await safeFetch(healthUrl, {
          timeout: TIMEOUTS.QUICK_CHECK,
        });

        const responseTime = Date.now() - startTime;

        healthResults[service.key] = {
          status: STATUS_TYPES.HEALTHY,
          responseTime,
          message: 'Сервис доступен',
          data: response,
          timestamp: new Date().toISOString(),
        };

        dispatch(
          addLog({
            msg: `✅ ${service.name} доступен (${responseTime}ms)`,
            type: 'success',
            data: { service: service.key, responseTime },
          })
        );
      } catch (error) {
        healthResults[service.key] = {
          status: STATUS_TYPES.UNHEALTHY,
          error: error.message,
          message: `Сервис недоступен: ${error.message}`,
          timestamp: new Date().toISOString(),
        };

        dispatch(
          addLog({
            msg: `❌ ${service.name} недоступен: ${error.message}`,
            type: 'error',
            data: { service: service.key, error: error.message },
          })
        );
      }
    });

    await Promise.allSettled(healthPromises);

    Object.entries(healthResults).forEach(([key, diagnostics]) => {
      dispatch(
        updateNetworkDiagnostics({
          serviceKey: key,
          diagnostics,
        })
      );
    });

    const healthyCount = Object.values(healthResults).filter(
      (r) => r.status === STATUS_TYPES.HEALTHY
    ).length;
    const totalCount = Object.keys(healthResults).length;

    dispatch(
      addLog({
        msg: `✅ Проверка завершена: ${healthyCount}/${totalCount} сервисов доступны`,
        type: healthyCount === totalCount ? 'success' : 'warning',
        data: { healthyCount, totalCount, results: healthResults },
      })
    );

    return healthResults;
  } catch (error) {
    dispatch(
      addLog({
        msg: `💥 Критическая ошибка проверки: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );
    throw error;
  } finally {
    dispatch(setHealthCheckLoading(false));
  }
};

export const performCompleteHealthCheck = () => async (dispatch) => {
  try {
    dispatch(setHealthCheckLoading(true));

    dispatch(
      addLog({
        msg: '🚀 Запуск полной проверки системы...',
        type: 'info',
      })
    );

    const [servicesResult, infrastructureResult] = await Promise.allSettled([
      dispatch(performQuickHealthCheck()),
      dispatch(checkInfrastructureAPI()),
    ]);

    const results = {
      services:
        servicesResult.status === 'fulfilled'
          ? servicesResult.value
          : servicesResult.reason,
      infrastructure:
        infrastructureResult.status === 'fulfilled'
          ? infrastructureResult.value
          : infrastructureResult.reason,
    };

    dispatch(
      addLog({
        msg: '✅ Полная проверка системы завершена',
        type: 'success',
        data: results,
      })
    );

    return results;
  } catch (error) {
    dispatch(
      addLog({
        msg: `💥 Ошибка полной проверки системы: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );
    throw error;
  } finally {
    dispatch(setHealthCheckLoading(false));
  }
};

export const sendRestRequest = (requestData) => async (dispatch) => {
  const { url, method, body, serviceKey } = requestData;

  if (!url) {
    dispatch(addLog({ msg: '❌ URL запроса не указан', type: 'error' }));
    return;
  }

  try {
    dispatch(setRestLoading(true));

    dispatch(
      addLog({
        msg: `🔄 Отправка ${method} запроса к ${url}`,
        type: 'info',
      })
    );

    const startTime = Date.now();
    const response = await safeFetch(url, {
      method,
      body: body ? JSON.stringify(body) : undefined,
      timeout: TIMEOUTS.REST_REQUEST,
    });

    const responseTime = Date.now() - startTime;

    const result = {
      status: STATUS_TYPES.SUCCESS,
      response,
      responseTime,
      serviceKey,
      timestamp: new Date().toISOString(),
    };

    dispatch(
      addLog({
        msg: `✅ REST запрос выполнен успешно (${responseTime}ms)`,
        type: 'success',
        data: result,
      })
    );

    return result;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка REST запроса: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );
    throw error;
  } finally {
    dispatch(setRestLoading(false));
  }
};

// ==================== ИНФРАСТРУКТУРА THUNKS ====================

export const checkInfrastructureAPI = () => async (dispatch) => {
  try {
    dispatch(setHealthCheckLoading(true));

    dispatch(
      addLog({
        msg: '🔄 Запуск проверки инфраструктуры через ImCon API...',
        type: 'info',
      })
    );

    const [rabbitResult, tarantoolResult, mongoResult, kratosResult, kratosAdminResult] =
      await Promise.allSettled([
        dispatch(checkRabbitMQHealth()),
        dispatch(checkTarantoolHealth()),
        dispatch(checkMongoDBHealth()),
        dispatch(checkKratosHealth()),
        dispatch(checkKratosAdminHealth()),
      ]);

    const results = {
      rabbitmq:
        rabbitResult.status === 'fulfilled' ? rabbitResult.value : rabbitResult.reason,
      tarantool:
        tarantoolResult.status === 'fulfilled'
          ? tarantoolResult.value
          : tarantoolResult.reason,
      mongodb:
        mongoResult.status === 'fulfilled' ? mongoResult.value : mongoResult.reason,
      kratos:
        kratosResult.status === 'fulfilled' ? kratosResult.value : kratosResult.reason,
      kratos_admin:
        kratosAdminResult.status === 'fulfilled'
          ? kratosAdminResult.value
          : kratosAdminResult.reason,
    };

    const healthyCount = Object.values(results).filter(
      (r) =>
        r &&
        (r.status === STATUS_TYPES.CONNECTED ||
          r.status === STATUS_TYPES.HEALTHY ||
          r.status === STATUS_TYPES.SUCCESS)
    ).length;
    const totalCount = Object.keys(results).length;

    dispatch(
      addLog({
        msg: `✅ Проверка инфраструктуры через API завершена: ${healthyCount}/${totalCount} компонентов доступны`,
        type: healthyCount === totalCount ? 'success' : 'warning',
        data: { results, healthyCount, totalCount },
      })
    );

    return results;
  } catch (error) {
    dispatch(
      addLog({
        msg: `💥 Ошибка проверки инфраструктуры через API: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );
    throw error;
  } finally {
    dispatch(setHealthCheckLoading(false));
  }
};

// ==================== RABBITMQ THUNKS ====================

export const checkRabbitMQHealth = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.RABBITMQ_HEALTH}`;

    dispatch(addLog({ msg: '🔄 Проверка RabbitMQ через ImCon API...', type: 'info' }));

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setRabbitMQStatus(status));

    dispatch(
      addLog({
        msg: `✅ RabbitMQ проверен: ${data.status}`,
        type: data.status === STATUS_TYPES.CONNECTED ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setRabbitMQStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка проверки RabbitMQ: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkRabbitMQDetailed = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.RABBITMQ_DETAILED}`;

    dispatch(
      addLog({ msg: '🔄 Детальная проверка RabbitMQ через ImCon API...', type: 'info' })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setRabbitMQStatus(status));

    dispatch(
      addLog({
        msg: `✅ Детальная проверка RabbitMQ: ${data.status}`,
        type: data.status === STATUS_TYPES.CONNECTED ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка детальной проверки RabbitMQ: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return { status: STATUS_TYPES.ERROR, error: error.message };
  }
};

export const testRabbitMQPublish = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.RABBITMQ_TEST}`;

    dispatch(
      addLog({
        msg: '🧪 Тестирование публикации в RabbitMQ через ImCon API...',
        type: 'info',
      })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    dispatch(
      addLog({
        msg: `🧪 Тест публикации RabbitMQ: ${data.status}`,
        type: data.status === STATUS_TYPES.SUCCESS ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка теста публикации RabbitMQ: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return { status: STATUS_TYPES.ERROR, error: error.message };
  }
};

// ==================== TARANTOOL THUNKS ====================

export const checkTarantoolHealth = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.TARANTOOL_HEALTH}`;

    dispatch(addLog({ msg: '🔄 Проверка Tarantool через ImCon API...', type: 'info' }));

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setTarantoolStatus(status));

    dispatch(
      addLog({
        msg: `✅ Tarantool проверен: ${data.status}`,
        type: data.status === STATUS_TYPES.CONNECTED ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setTarantoolStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка проверки Tarantool: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkTarantoolDetailed = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.TARANTOOL_DETAILED}`;

    dispatch(
      addLog({ msg: '🔄 Детальная проверка Tarantool через ImCon API...', type: 'info' })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setTarantoolStatus(status));

    dispatch(
      addLog({
        msg: `✅ Детальная проверка Tarantool: ${data.status}`,
        type: data.status === STATUS_TYPES.CONNECTED ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка детальной проверки Tarantool: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return { status: STATUS_TYPES.ERROR, error: error.message };
  }
};

// ==================== MONGODB THUNKS ====================

export const checkMongoDBHealth = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.MONGODB_HEALTH}`;

    dispatch(addLog({ msg: '🔄 Проверка MongoDB через ImCon API...', type: 'info' }));

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setMongoDBStatus(status));

    dispatch(
      addLog({
        msg: `✅ MongoDB проверена: ${data.status}`,
        type: data.status === STATUS_TYPES.CONNECTED ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setMongoDBStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка проверки MongoDB: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkMongoDBDetailed = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.MONGODB_DETAILED}`;

    dispatch(
      addLog({ msg: '🔄 Детальная проверка MongoDB через ImCon API...', type: 'info' })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setMongoDBStatus(status));

    dispatch(
      addLog({
        msg: `✅ Детальная проверка MongoDB: ${data.status}`,
        type: data.status === STATUS_TYPES.CONNECTED ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка детальной проверки MongoDB: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return { status: STATUS_TYPES.ERROR, error: error.message };
  }
};

// ==================== CLICKHOUSE THUNKS ====================

export const checkClickHouseHealth = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}/api/health/clickhouse`;

    dispatch(addLog({ msg: '🔄 Проверка ClickHouse через ImCon API...', type: 'info' }));

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setClickHouseStatus(status));

    dispatch(
      addLog({
        msg: `✅ ClickHouse проверен: ${data.status}`,
        type: data.status === STATUS_TYPES.HEALTHY ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setClickHouseStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка проверки ClickHouse: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkClickHouseDetailed = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}/api/health/clickhouse/detailed`;

    dispatch(
      addLog({ msg: '🔄 Детальная проверка ClickHouse через ImCon API...', type: 'info' })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setClickHouseStatus(status));

    dispatch(
      addLog({
        msg: `✅ Детальная проверка ClickHouse: ${data.status}`,
        type: data.status === STATUS_TYPES.HEALTHY ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка детальной проверки ClickHouse: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return { status: STATUS_TYPES.ERROR, error: error.message };
  }
};

// ==================== FEATURE TREE (DEPRECATED) ====================

/**
 * @deprecated — Feature Tree больше не проверяется через `/api/health/feature-tree/generate`.
 * Используйте `checkImageLangFullReport` (секция `feature_tree_manager`).
 *
 * Функция оставлена для обратной совместимости (импортируется в `ServicesTester.jsx`).
 * Возвращает `{ status: 'deprecated', message: ... }` без побочных эффектов.
 */
export const checkFeatureTreeGeneration = () => async (dispatch) => {
  const status = {
    status: 'deprecated',
    message:
      'Feature Tree теперь проверяется через ImageLang → секция `feature_tree_manager`',
    lastChecked: new Date().toISOString(),
  };

  dispatch(setFeatureTreeStatus(status));

  dispatch(
    addLog({
      msg: '⚠️ checkFeatureTreeGeneration устарел (используйте ImageLang → Отчёт)',
      type: 'warning',
    })
  );

  return status;
};

// ==================== KRATOS THUNKS ====================

export const checkKratosHealth = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.KRATOS_HEALTH}`;

    dispatch(addLog({ msg: '🔄 Проверка Kratos Public API через ImCon...', type: 'info' }));

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosPublicStatus(status));

    dispatch(
      addLog({
        msg: `✅ Kratos Public API: ${data.status || 'available'}`,
        type: data.status === STATUS_TYPES.HEALTHY ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosPublicStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка проверки Kratos Public API: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkKratosAdminHealth = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.KRATOS_ADMIN_HEALTH}`;

    dispatch(addLog({ msg: '🔄 Проверка Kratos Admin API через ImCon...', type: 'info' }));

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosAdminStatus(status));

    dispatch(
      addLog({
        msg: `✅ Kratos Admin API: ${data.status || 'available'}`,
        type: data.status === STATUS_TYPES.HEALTHY ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosAdminStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка проверки Kratos Admin API: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkKratosUsers = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.KRATOS_USERS}`;

    dispatch(
      addLog({
        msg: '🔄 Получение статистики пользователей Kratos через ImCon...',
        type: 'info',
      })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosUsersStatus(status));

    dispatch(
      addLog({
        msg: `✅ Статистика пользователей Kratos получена`,
        type: data.status === STATUS_TYPES.SUCCESS ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    const errorStatus = {
      status: STATUS_TYPES.ERROR,
      error: error.message,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosUsersStatus(errorStatus));

    dispatch(
      addLog({
        msg: `❌ Ошибка получения статистики пользователей Kratos: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return errorStatus;
  }
};

export const checkKratosDetailed = () => async (dispatch) => {
  try {
    const url = `${SERVICE_URLS.IM_CON}${API_ENDPOINTS.KRATOS_DETAILED}`;

    dispatch(
      addLog({ msg: '🔄 Детальная диагностика Kratos через ImCon...', type: 'info' })
    );

    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    const status = {
      ...data,
      lastChecked: new Date().toISOString(),
    };

    dispatch(setKratosPublicStatus(status));

    dispatch(
      addLog({
        msg: `✅ Детальная диагностика Kratos завершена`,
        type: data.status === STATUS_TYPES.SUCCESS ? 'success' : 'warning',
        data: data,
      })
    );

    return data;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка детальной диагностики Kratos: ${error.message}`,
        type: 'error',
        data: { error: error.message },
      })
    );

    return { status: STATUS_TYPES.ERROR, error: error.message };
  }
};

// ==================== IMAGE LANG THUNKS ====================

/**
 * Полный отчёт ImageLangDiagnostics (v10.8).
 * Возвращает `{ status, summary, sections, diagnostics_time_ms }`.
 *
 * Используется в `ImageLangPanel` (таб «Отчёт»).
 * Не пишет в Redux (панель держит state локально).
 */
export const checkImageLangFullReport = () => async (dispatch) => {
  dispatch(
    addLog({
      msg: '🖼️ Запуск полной диагностики ImageLang (v10.8)...',
      type: 'info',
    })
  );

  try {
    const url = `${SERVICE_URLS.IM_CON}/api/health/image_lang/comprehensive`;
    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    if (data && data.success && data.data) {
      dispatch(
        addLog({
          msg: `✅ ImageLang: ${data.data.status} (${data.data.summary?.passed}/${data.data.summary?.total_checks})`,
          type:
            data.data.status === 'healthy'
              ? 'success'
              : data.data.status === 'degraded'
              ? 'warning'
              : 'error',
          data: { summary: data.data.summary },
        })
      );
      return data.data;
    }

    const errMsg = data?.error || data?.message || 'Unexpected response';
    dispatch(addLog({ msg: `❌ ImageLang: ${errMsg}`, type: 'error' }));
    return null;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка полной диагностики ImageLang: ${error.message}`,
        type: 'error',
      })
    );
    return null;
  }
};

/**
 * Одна секция ImageLangDiagnostics.
 *
 * @param {string} name — имя секции (например, `encoder_decoder`, `feature_tree_manager`)
 * @returns {Promise<object|null>} — `{ checks, total, passed, failed }`
 */
export const checkImageLangSection = (name) => async (dispatch) => {
  if (!name || typeof name !== 'string') {
    dispatch(addLog({ msg: '❌ checkImageLangSection: пустое имя', type: 'error' }));
    return null;
  }

  dispatch(addLog({ msg: `🖼️ ImageLang секция: ${name}`, type: 'info' }));

  try {
    const url = `${SERVICE_URLS.IM_CON}/api/health/image_lang/section/${encodeURIComponent(name)}`;
    const data = await safeFetch(url, { timeout: TIMEOUTS.DETAILED_CHECK });

    if (data && data.success && data.data) {
      dispatch(
        addLog({
          msg: `✅ Секция ${name}: ${data.data.passed}/${data.data.total}`,
          type: data.data.failed === 0 ? 'success' : 'warning',
        })
      );
      return data.data;
    }

    const errMsg = data?.error || data?.message || 'Unexpected response';
    dispatch(addLog({ msg: `❌ Секция ${name}: ${errMsg}`, type: 'error' }));
    return null;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка секции ${name}: ${error.message}`,
        type: 'error',
      })
    );
    return null;
  }
};

/**
 * Краткая статистика ImageLang (`/api/health/image_lang`).
 * Возвращает `{ status, summary }`.
 */
export const checkImageLangStats = () => async (dispatch) => {
  dispatch(addLog({ msg: '🖼️ ImageLang: краткая статистика…', type: 'info' }));

  try {
    const url = `${SERVICE_URLS.IM_CON}/api/health/image_lang`;
    const data = await safeFetch(url, { timeout: TIMEOUTS.QUICK_CHECK });

    if (data && data.success && data.data) {
      return data.data;
    }
    return null;
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка статистики ImageLang: ${error.message}`,
        type: 'error',
      })
    );
    return null;
  }
};

// ==================== IMAGE LANG CODEC TEST ====================

/**
 * Быстрый тест кодека ImageLang (encode → decode → roundtrip).
 * Используется в `MonitoringTab` (кнопка «Тест ImageLang кодека»).
 */
export const testImageLangCodec = () => async (dispatch) => {
  dispatch(
    addLog({
      msg: '🔍 Запуск теста семантического кодека ImageLang...',
      type: 'info',
    })
  );

  const testFeatures = {
    type: 'object',
    complexity: 'сложный',
    life: 'живое',
    scale: 'норма',
    race_code: 'Terran_Homo_Sapiens',
    brightness: 'яркий',
    distance: 'близко',
  };

  try {
    const encodeRes = await safeFetch(`${SERVICE_URLS.IM_CON}/api/image-lang/encode`, {
      method: HTTP_METHODS.POST,
      body: JSON.stringify({ features: testFeatures }),
      timeout: TIMEOUTS.QUICK_CHECK,
    });

    if (encodeRes && encodeRes._error) {
      throw new Error(`Ошибка кодирования: ${encodeRes.status} ${encodeRes.statusText}`);
    }

    if (!encodeRes || !encodeRes.bit_id) {
      throw new Error('Неверный ответ от /encode: нет bit_id');
    }

    const decodeRes = await safeFetch(`${SERVICE_URLS.IM_CON}/api/image-lang/decode`, {
      method: HTTP_METHODS.POST,
      body: JSON.stringify({ bit_id: encodeRes.bit_id }),
      timeout: TIMEOUTS.QUICK_CHECK,
    });

    if (decodeRes && decodeRes._error) {
      throw new Error(`Ошибка декодирования: ${decodeRes.status} ${decodeRes.statusText}`);
    }

    if (!decodeRes || !decodeRes.features) {
      throw new Error('Неверный ответ от /decode: нет features');
    }

    const { features } = decodeRes;
    const roundtripOk =
      features.type === 'object' &&
      features.complexity === 'сложный' &&
      features.life === 'живое' &&
      features.scale === 'норма' &&
      features.race_code === 'Terran_Homo_Sapiens' &&
      features.brightness === 'яркий' &&
      features.distance === 'близко';

    if (roundtripOk) {
      dispatch(
        addLog({
          msg: `✅ ImageLang кодек работает корректно\nbit_id: ${encodeRes.bit_id}`,
          type: 'success',
          data: { encode: encodeRes, decode: decodeRes },
        })
      );
    } else {
      dispatch(
        addLog({
          msg: '⚠️ ImageLang кодек работает, но данные не совпадают после декодирования',
          type: 'warning',
          data: {
            expected: testFeatures,
            actual: features,
            encode: encodeRes,
            decode: decodeRes,
          },
        })
      );
    }
  } catch (error) {
    dispatch(
      addLog({
        msg: `❌ Ошибка теста ImageLang кодека: ${error.message}`,
        type: 'error',
        data: { error: error.message, stack: error.stack },
      })
    );
  }
};

// Алиас для обратной совместимости
export const checkInfrastructure = checkInfrastructureAPI;
